<template>
    <div id="coupons-report-page" class="px-3">
        <div class="top filter-bar-container px-0">
            <div class="title-button">
                <h1>{{ t('couponsReport-title') }}</h1>
            </div>
            <filter-bar
                :has-period-filter="false"
                :has-branch-filter="canFilterPerBranch"
                :has-client-filter="true"
                :show-labels="true"
                :update-query-string="true"
                ref="filterBar"
                :filters = "extraFilters"
                @changed="updateFilters"
            />
        </div>
        <div class="content box" v-if="loading">
            <loader />
        </div>

        <template v-if="ready">
            <grid
                source="coupons/report"
                :filters="filters"
                :displayItemsPerPage="true"
                :quantityPerPage="itemsPerPage"
                :show-search="false"
            >
                <template v-slot:grid-actions>
                    <div>
                        <button
                            class="btn btn-primary"
                            :disabled="exporting"
                            @click="exportCoupons"
                            :aria-label="t('couponsReport-export_aria_label')"
                            style="display: flex; gap: 4px; align-items: center; justify-content: center;"
                        >
                            <Icon icon-class="ui-download-01" size="5" />
                            <span>{{ t('couponsReport-export_button') }}</span>
                        </button>
                    </div>
                </template>
                <template v-slot:rows="grid">
                    <thead>
                        <tr>
                            <grid-th
                                :grid="grid"
                                :sortable="false"
                                attr="coupon"
                                >{{ t('couponsReport-table-coupon') }}</grid-th
                            >
                            <grid-th
                                :grid="grid"
                                :sortable="false"
                                attr="client_name"
                                >{{ t('couponsReport-table-client') }}</grid-th
                            >
                            <grid-th
                                :grid="grid"
                                :sortable="false"
                                attr="whatsapp"
                                >{{ t('couponsReport-table-phone') }}</grid-th
                            >
                            <grid-th :grid="grid" :sortable="false" attr="email"
                                >{{ t('couponsReport-table-email') }}</grid-th
                            >
                            <grid-th
                                :grid="grid"
                                :sortable="true"
                                attr="created"
                                >{{ t('couponsReport-table-created_at') }}</grid-th
                            >
                            <grid-th
                                :grid="grid"
                                :sortable="true"
                                attr="expiration_date"
                                >{{ t('couponsReport-table-validity_between') }}</grid-th
                            >
                            <grid-th
                                :grid="grid"
                                :sortable="true"
                                attr="used_date"
                                >{{ t('couponsReport-table-used_at') }}</grid-th
                            >
                            <grid-th
                                :grid="grid"
                                :sortable="true"
                                attr="automation_title"
                                >{{ t('couponsReport-table-automation') }}</grid-th
                            >
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="coupon in grid.data" :key="coupon.id">
                            <td style="min-width: 400px">
                                <div class="badges">
                                    <div
                                        class="badge badge-coupon badge-secondary"
                                    >
                                        {{ coupon.code }}
                                    </div>
                                    <div
                                        class="badge badge-secondary"
                                        v-if="coupon.used != '0'"
                                    >
                                        {{ t('couponsReport-badges-used') }}
                                        <human-date
                                            :date="coupon.used_date"
                                            format="[d]/[M]/[Y] [h]:[m]"
                                        />
                                    </div>
                                    <div
                                        class="badge badge-warning"
                                        v-if="coupon.expired"
                                    >
                                        {{ t('couponsReport-badges-expired') }}
                                    </div>
                                    <button
                                        class="badge badge-primary"
                                        style="border: none"
                                        @click="
                                            $store.dispatch(
                                                'coupons/validateCode',
                                                coupon.code,
                                            )
                                        "
                                        v-if="coupon.valid"
                                    >
                                        {{ t('couponsReport-badges-valid_until') }}
                                        <human-date
                                            :date="coupon.expiration_date"
                                            format="[d]/[M]/[Y] [h]:[m]"
                                        />
                                    </button>
                                    <button
                                        class="badge badge-primary"
                                        style="border: none"
                                        @click="$store.dispatch('coupons/validateCode',coupon.code)"
                                        v-if="coupon.valid"
                                    >
                                        <span class="fa fa-check"></span>
                                    </button>
                                </div>
                                <span class="coupon-text">{{
                                    coupon.description || coupon.text
                                }}</span>
                            </td>
                            <td
                                style="min-width: 130px; cursor: pointer"
                                @click="$router.push('/client/' + coupon.clients_id)"
                            >
                                {{ coupon.client_name || t('couponsReport-empty-no_name') }}
                                <span class="far fa-link"></span>
                            </td>
                            <td style="min-width: 150px">
                                <btn-whatsapp
                                    :number="coupon.client_whatsapp"
                                    v-if="coupon.client_whatsapp"
                                />
                                {{ coupon.client_whatsapp }}
                            </td>
                            <td style="min-width: 150px">
                                {{ coupon.client_email }}
                            </td>

                            <td style="min-width: 160px">
                                <human-date
                                    :date="coupon.created"
                                    format="[d]/[M]/[Y] [h]:[m]"
                                />
                            </td>

                            <td style="min-width: 160px">
                                <human-date
                                    :date="coupon.valid_from"
                                    format="[d]/[M]/[Y] [h]:[m]"
                                />
                                {{ t('couponsReport-conjunction_and') }}
                                <human-date
                                    :date="coupon.expiration_date"
                                    format="[d]/[M]/[Y] [h]:[m]"
                                />
                            </td>

                            <td v-if="coupon.used_date" style="min-width: 160px">
                                <human-date
                                    :date="coupon.used_date"
                                    format="[d]/[M]/[Y] [h]:[m]"
                                />
                            </td>
                            <td v-else>
                                {{ t('couponsReport-empty-not_used') }}
                            </td>

                            <td >
                                {{ coupon.automation_title ?? t('couponsReport-empty-no_automation') }}
                            </td>

                        </tr>
                    </tbody>
                </template>
            </grid>
        </template>
        <template v-else>
            <h2>{{ t('couponsReport-empty-no_data_for_period') }}</h2>
        </template>
    </div>
</template>
<script>
import keviApi from "../keviApi";
import HumanDate from "../components/human-date/human-date";
import { mapState } from "vuex";
import BtnWhatsapp from "../components/btn-whatsapp/btn-whatsapp";
import saveData from "../helpers/saveData";
import Papa from "papaparse";
import { formatInstantInPlaceTz } from "../helpers/datetime";
import Icon from "../components/icon/icon";
export default {
    components: { BtnWhatsapp, HumanDate, Icon },
    data() {
        return {
            coupons: [],
            loading: false,
            exporting: false,
            ready: false,
            filters: [],
            extraFilters: [],
            itemsPerPage: 20,
            automations: [],
        };
    },
    async mounted() {
        const filterBar = this.$refs.filterBar;
        if (this.$module("segments")) {
            let options = keviApi.getOptions("segments");
            const response = await this.$http(options);
            let segments = response.body;
            this.extraFilters.push({
                component: "checkbox",
                label: this.t("couponsReport-filters-client_segment"),
                name: "client-segments",
                options: {
                    options: segments.map((seg) => ({
                        value: seg.id,
                        label: seg.name,
                    })),
                    icon: "fa-tag",
                },
                removable: true,
            });
        }
        this.extraFilters.push({
            component: "textfield",
            label: this.t("couponsReport-filters-coupon_code"),
            name: "coupon_code",
            removable: true,
        })
        this.extraFilters.push({
            component: "period",
            label: this.t("couponsReport-filters-created_date"),
            name: "created_period",
            removable: true,
        });
        this.extraFilters.push({
            component: "period",
            label: this.t("couponsReport-filters-expiration_date"),
            name: "expiration_period",
            removable: true,
        });
        this.extraFilters.push({
            component: "period",
            label: this.t("couponsReport-filters-used_date"),
            name: "used_period",
            removable: true,
        });

        if (
            this.$module("automations") &&
            this.user?.permissions?.automations?.enabled
        ) {
            try {
                await this.loadAutomations();

                this.extraFilters.push({
                component: "selectbox",
                label: this.t("couponsReport-filters-automation"),
                name: "automation_id",
                options: (this.automations ?? []).map((automation) => ({
                    value: automation.id,
                    label: automation.title,
                })),
                removable: true,
                });
            } catch (error) {
                console.error("Error in automations load", error);
            }
        }

        this.extraFilters.push({
            component: "selectbox",
            label: this.t("couponsReport-filters-status"),
            name: "status",
            options: [
                {
                    value: "available",
                    label: this.t("couponsReport-filters-available"),
                },
                {
                    value: "used",
                    label: this.t("couponsReport-filters-used"),
                },
                {
                    value: "expired",
                    label: this.t("couponsReport-filters-expired"),
                },
            ],
            removable: true,
        });

        filterBar.addFilter("status");
        if (!filterBar.values?.status) {
            filterBar.setValue("status", "available");
        }
        this.$nextTick(() => {
          this.ready = true;
        });


    },
    created() {
        document.getElementsByTagName("title")[0].innerText = this.t(
            "couponsReport-page_title",
        );
    },
    computed: {
        ...mapState({
            user: (state) => state.user,
        }),
        ...mapState("placeDetails", ["users", "roles", "branches"]),
        placeId() {
            return this.$store.state.place.id;
        },
        canFilterPerBranch() {
            return !!this.user.permissions["reports"]["options"][
                "global_access"
            ];
        },
        hasBranchFilter() {
            return true;
        },
        hasClientFilter() {
            return true;
        },
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        updateFilters: function (filters) {
            this.filters = filters;
        },
        formatNumber(number, decimals = 2) {
            return parseFloat(number).toLocaleString("pt-BR", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            });
        },
        exportCoupons() {
            const options = keviApi.getOptions("coupons/report", "GET", {
                filters: this.filters,
                export: true,
            });
            this.exporting = true;
            const tz = this.$store.state.place.timezone;
            const locale = this.$store.state.place.locale;
            this.$http(options).then((response) => {
                const coupons = response.body.data.map((coupon) => {
                    let status = this.t("couponsReport-export-status_before_validity");
                    if (parseInt(coupon.used) === 1) {
                        status = this.t("couponsReport-export-status_used");
                    } else if (coupon.expired) {
                        status = this.t("couponsReport-export-status_expired");
                    } else if (coupon.valid) {
                        status = this.t("couponsReport-export-status_valid");
                    }
                    return {
                        [this.t("couponsReport-export-column_code")]: coupon.code,
                        [this.t("couponsReport-export-column_coupon")]:
                            coupon.description,
                        [this.t("couponsReport-export-column_status")]: status,
                        [this.t("couponsReport-export-column_valid_from")]:
                            formatInstantInPlaceTz(coupon.valid_from, tz, locale),
                        [this.t("couponsReport-export-column_valid_until")]:
                            formatInstantInPlaceTz(coupon.expiration_date, tz, locale),
                        [this.t("couponsReport-export-column_used_at")]:
                            coupon.used_date
                                ? formatInstantInPlaceTz(coupon.used_date, tz, locale)
                                : this.t("couponsReport-empty-not_used"),
                        [this.t("couponsReport-export-column_client_name")]:
                            coupon.client_name,
                        [this.t("couponsReport-export-column_client_phone")]:
                            coupon.client_whatsapp,
                        [this.t("couponsReport-export-column_client_email")]:
                            coupon.client_email,
                        [this.t("couponsReport-export-column_automation")]:
                            coupon.automation_title,
                    };
                });
                const csv = Papa.unparse(coupons);
                saveData(csv, "coupons.csv");
                this.exporting = false;
            });
        },
        loadReport(filters) {
            this.loading = true;
            this.filters = Object.assign({}, filters);

            const options = keviApi.getOptions("coupons/report", "GET", {
                filters,
            });
            this.$http(options).then((response) => {
                this.loading = false;
                this.coupons = response.body.data;
            });
        },
        nl2br(str) {
            if (!str) return "";
            return (str + "").replace(
                /([^>\r\n]?)(\r\n|\n\r|\r|\n)/g,
                "$1" + "<br />" + "$2",
            );
        },
        async loadAutomations() {
            const options = keviApi.getOptions("automations", "GET");
            const response = (await this.$http(options)).body;
            this.automations = response.automations || [];
        },
    },
};
</script>
<style scoped>
::v-deep .current-filters {
    padding-bottom: 0;
}
.table tr,
.table tr td {
    vertical-align: middle;
}
.badge {
    padding: 5px;
    font-weight: 500;
    font-size: 12px;
}
.coupon-text {
    font-size: 15px;
    margin-top: 10px;
    display: inline-block;
}
</style>
